import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { isObject } from 'lodash';

import { SeatKillsTableSessionRow } from './SeatKillsTableSessionRow';
import { SeatKillsTableSportVenueRow } from './SeatKillsTableSportVenueRow';

import Button from 'CommonComponents/Button';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import EditIcon from 'Assets/icons/edit.svg';

export const SeatKillsTable = (props) => {
    const [expanded, setExpanded] = useState({});
    const [edited, setEdited] = useState({});
    const [editedAll, setEditedAll] = useState(false);

    const isEditing = useMemo(() => editedAll || Object.values(edited).some(Boolean), [editedAll, edited]);
    const isEditable = useMemo(
        () => props.categories.kills.length + props.categories.holds.length > 0,
        [props.categories.kills.length, props.categories.holds.length],
    );

    useEffect(() => {
        defaultEdited();
    }, [props.events]);

    useEffect(() => {
        defaultExpanded();
    }, [props.sportVenues]);

    const defaultEdited = () => {
        const datum = JSON.parse(JSON.stringify(edited));

        props.events.forEach((event) => {
            const eventId = event.sport.id;
            const venues = event.venues ?? [];
            venues.forEach((venue) => {
                const venueId = venue.venue.id;
                const sessions = venue.data.schedules;

                const typedVenueId = getTypedId(eventId, venueId);
                if (!(typedVenueId in datum)) {
                    datum[typedVenueId] = false;
                }

                Object.keys(sessions).forEach((sessionId) => {
                    const typedSessionId = getTypedId(eventId, venueId, sessionId);
                    if (!(typedSessionId in datum)) {
                        datum[typedSessionId] = false;
                    }
                });
            });
        });

        setEdited(datum);
    };

    const getTypedId = (eventId, venueId, sessionId = null) => {
        let typedId = eventId + '::' + venueId;
        if (sessionId) {
            typedId += '::' + sessionId;
        }

        return typedId;
    };

    const toggleEditRow = (edit, eventId, venueId, sessionId = null) => {
        const datum = JSON.parse(JSON.stringify(edited));
        const id = getTypedId(eventId, venueId, sessionId);
        datum[id] = edit;

        setEdited(datum);
    };

    const editAll = (edit = true) => {
        const datum = JSON.parse(JSON.stringify(edited));
        Object.keys(datum).forEach((id) => {
            datum[id] = edit;
        });

        setEdited(datum);
        setEditedAll(edit);
    };

    const saveSeatKills = (data, eventId, venueId, sessionId = null) => {
        return props.saveSeatKills(data, () => {
            toggleEditRow(false, eventId, venueId, sessionId);
        });
    };

    const isRowEdited = (eventId, venueId, sessionId = null) => {
        const id = getTypedId(eventId, venueId, sessionId);

        return edited[id] ?? false;
    };

    const defaultExpanded = () => {
        const datum = JSON.parse(JSON.stringify(expanded));

        props.sportVenues.forEach((sportVenue) => {
            const eventId = sportVenue.id;
            if (!(eventId in datum)) {
                datum[eventId] = { expanded: true, venues: {} };
            }
            sportVenue.venues.forEach((venue) => {
                const venueId = venue.id;
                if (!(venueId in datum[eventId].venues)) {
                    datum[eventId].venues[venueId] = { expanded: false };
                }
            });
        });

        setExpanded(datum);
    };

    const toggleExpanded = (type, eventId, venueId = null) => {
        const datum = JSON.parse(JSON.stringify(expanded));

        if (type === 'venue' && venueId) {
            datum[eventId].venues[venueId].expanded = !datum[eventId].venues[venueId].expanded;
        } else {
            datum[eventId].expanded = !datum[eventId].expanded;
        }

        setExpanded(datum);
    };

    const toggleExpandAll = (expand = true) => {
        const datum = JSON.parse(JSON.stringify(expanded));

        Object.keys(datum).forEach((eventId) => {
            const venues = datum[eventId].venues;
            datum[eventId].expanded = expand;

            Object.keys(venues).forEach((venueId) => {
                datum[eventId].venues[venueId].expanded = expand;
            });
        });

        setExpanded(datum);
    };

    const colspanMainTable = useMemo(
        () => props.categories.kills.length + props.categories.holds.length + 9 + (isEditing ? 1 : 0),
        [props.categories, isEditing],
    );

    const calculateCategoryTotal = (category, data) => {
        let totalHolds = 0;
        let totalKills = 0;

        if (category !== 'holds') {
            Object.keys(data.kills).forEach((k) => {
                if (k !== 'total') {
                    const killData = data.kills[k];
                    totalKills += +killData.number;
                }
            });
        }
        if (category !== 'kills') {
            Object.keys(data.holds).forEach((k) => {
                if (k !== 'total') {
                    const holdsData = data.holds[k];
                    totalHolds += +holdsData.number;
                }
            });
        }

        return totalHolds + totalKills;
    };

    const calculateNetCapacity = (data) => {
        const capacity = data.capacity;
        const totalKillsHolds = calculateCategoryTotal('all', data);
        const net = capacity - totalKillsHolds;

        return net >= 0 ? net : 0;
    };

    return (
        <>
            <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-gap-x-2 tw-mb-6">
                <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                    <div className="tw-flex tw-gap-x-1.5">
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleExpandAll()}>
                            {$t('Expand all')}
                        </button>
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleExpandAll(false)}>
                            {$t('Collapse all')}
                        </button>
                    </div>
                </div>
                {!isEditing && isEditable && <div className="tw-text-sm tw-text-red/70">{$t('Double-click clear cells to edit numbers')}</div>}
                {isEditable && (
                    <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                        <Button variant={editedAll ? 'secondary' : 'primary'} onClick={editedAll ? () => editAll(false) : () => editAll()}>
                            <EditIcon className="tw-w-3 tw-h-3" />
                            {editedAll ? $t('Cancel Edit All') : $t('Edit All')}
                        </Button>
                    </div>
                )}
            </div>

            <div className="inbud-table_wrapper tw-overflow-y-auto tw-overflow-x-auto tw-relative tw-max-h-[70vh]">
                <table className="inbud-table inbud-table_first-two-sticky">
                    <thead className="tw-sticky tw-top-0 sticky-header" style={{ zIndex: 15 }}>
                        <tr className="*:tw-font-extrabold *:!tw-pb-1.5">
                            <th className="tw-min-w-48 tw-max-w-48">{$t('Event')}</th>
                            <th className="tw-min-w-48 tw-max-w-48">{$t('Venue')}</th>
                            <th className="tw-min-w-40">{$t('Temporary Capacity')}</th>
                            <th className="tw-min-w-40">{$t('Permanent Capacity')}</th>
                            <th className="tw-min-w-40">{$t('Total Gross Capacity')}</th>
                            {props.categories.kills.map((item) => (
                                <th key={item.id} className="tw-min-w-40">
                                    {item.name}
                                </th>
                            ))}
                            <th className="tw-min-w-40">{$t('Seat Kills - Total')}</th>
                            {props.categories.holds.map((item) => (
                                <th key={item.id} className="tw-min-w-40">
                                    {item.name}
                                </th>
                            ))}
                            <th className="tw-min-w-40">{$t('Seat Holds - Total')}</th>
                            <th className="tw-min-w-40">{$t('Total Kills and Holds')}</th>
                            <th className="tw-min-w-40">{$t('Net Capacity')}</th>
                            {isEditing && <th>{$t('Actions')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {props.loading && !props.events.length && <TableRowSkeleton columns={colspanMainTable} rows={5} />}
                        {props.events.map((event) => {
                            const eventData = event.sport;
                            const venuesData = event.venues;

                            const eventExpanded = expanded[eventData.id]?.expanded ?? true;

                            return (
                                <Fragment key={eventData.id}>
                                    <tr>
                                        <td
                                            className="tw-cursor-pointer tw-font-bold"
                                            style={{ zIndex: 10 }}
                                            onClick={() => toggleExpanded('event', eventData.id)}
                                        >
                                            <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                                <div>{eventData.name}</div>
                                                <div>{eventExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
                                            </div>
                                        </td>
                                        <td className="tw-w-48" style={{ zIndex: 10 }}></td>
                                        <td colSpan={colspanMainTable - 2}></td>
                                    </tr>
                                    {eventExpanded &&
                                        venuesData.map((row) => {
                                            const venueData = row.venue;
                                            const data = row.data;

                                            const hasSchedules = !!data.schedules && isObject(data.schedules);
                                            const schedules = hasSchedules ? data.schedules : {};

                                            const venueExpanded = eventExpanded && (expanded[eventData.id]?.venues[venueData.id]?.expanded ?? true);

                                            return (
                                                <Fragment key={venueData.id}>
                                                    <SeatKillsTableSportVenueRow
                                                        venueData={venueData}
                                                        eventData={eventData}
                                                        data={data}
                                                        hasSchedules={hasSchedules}
                                                        toggleExpanded={toggleExpanded}
                                                        categories={props.categories}
                                                        calculateCategoryTotal={calculateCategoryTotal}
                                                        calculateNetCapacity={calculateNetCapacity}
                                                        editRow={() => toggleEditRow(true, eventData.id, venueData.id)}
                                                        cancelEdit={() => toggleEditRow(false, eventData.id, venueData.id)}
                                                        onSave={(data) => saveSeatKills(data, eventData.id, venueData.id)}
                                                        edited={isRowEdited(eventData.id, venueData.id)}
                                                        showActions={isEditing}
                                                    />
                                                    {hasSchedules && venueExpanded && (
                                                        <tr className="!tw-bg-white">
                                                            <td colSpan={colspanMainTable} className="tw-py-3 tw-bg-white">
                                                                <h5 className="table__header">{$t('Sessions')}:</h5>
                                                                <div className="inbud-table_wrapper tw-overflow-x-auto tw-relative">
                                                                    <table className="inbud-table inbud-table_first-two-sticky">
                                                                        <thead className="tw-sticky tw-top-0" style={{ zIndex: 15 }}>
                                                                            <tr className="*:tw-font-extrabold *:!tw-pb-1.5">
                                                                                <th className="tw-min-w-48 tw-max-w-48">{$t('Event')}</th>
                                                                                <th className="tw-min-w-48 tw-max-w-48">{$t('Venue')}</th>
                                                                                <th className="tw-min-w-32">{$t('Session')}</th>
                                                                                <th className="tw-min-w-28">{$t('Type')}</th>
                                                                                <th className="tw-min-w-40">{$t('Total Gross Capacity')}</th>
                                                                                {props.categories.kills.map((item) => (
                                                                                    <th key={`session_${item.id}`} className="tw-min-w-40">
                                                                                        {item.name}
                                                                                    </th>
                                                                                ))}
                                                                                <th className="tw-min-w-40">{$t('Seat Kills - Total')}</th>
                                                                                {props.categories.holds.map((item) => (
                                                                                    <th key={`session_${item.id}`} className="tw-min-w-40">
                                                                                        {item.name}
                                                                                    </th>
                                                                                ))}
                                                                                <th className="tw-min-w-40">{$t('Seat Holds - Total')}</th>
                                                                                <th className="tw-min-w-40">{$t('Total Kills and Holds')}</th>
                                                                                <th className="tw-min-w-40">{$t('Net Capacity')}</th>
                                                                                {isEditing && <th>{$t('Actions')}</th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {Object.keys(schedules).map((scheduleId) => {
                                                                                const schedule = schedules[scheduleId];
                                                                                return (
                                                                                    <SeatKillsTableSessionRow
                                                                                        key={`schedule_${scheduleId}`}
                                                                                        scheduleId={scheduleId}
                                                                                        schedule={schedule}
                                                                                        categories={props.categories}
                                                                                        data={data}
                                                                                        calculateCategoryTotal={calculateCategoryTotal}
                                                                                        calculateNetCapacity={calculateNetCapacity}
                                                                                        editRow={() =>
                                                                                            toggleEditRow(true, eventData.id, venueData.id, scheduleId)
                                                                                        }
                                                                                        cancelEdit={() =>
                                                                                            toggleEditRow(false, eventData.id, venueData.id, scheduleId)
                                                                                        }
                                                                                        onSave={(data) =>
                                                                                            saveSeatKills(data, eventData.id, venueData.id, scheduleId)
                                                                                        }
                                                                                        edited={isRowEdited(eventData.id, venueData.id, scheduleId)}
                                                                                        showActions={isEditing}
                                                                                    />
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
};
